import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, X, Menu } from "lucide-react";
import LogoMark from "@/components/LogoMark";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { label: "The Shop", id: "#about" },
  { label: "What We Do", id: "#services" },
  { label: "The Work", id: "#work" },
  { label: "Contact", id: "#contact" },
];

const go = (e, id) => {
  e.preventDefault();
  document.getElementById("mobile-menu")?.click?.();
  scrollToId(id);
};

export default function Nav() {
  const [open, setOpen] = useState(false);

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-bone/10 bg-charcoal/85 backdrop-blur-md">
      <nav className="mx-auto flex h-16 max-w-[1600px] items-center justify-between px-5 sm:px-8">
        <a
          href="#top"
          data-testid="nav-logo-link"
          className="flex items-center gap-3"
          onClick={(e) => {
            e.preventDefault();
            scrollToId("#top");
          }}
        >
          <LogoMark className="h-11" />
          <span className="font-display text-2xl font-bold uppercase leading-none tracking-[0.02em] text-bone">
            Dreamworlds <span className="text-steel">Garage</span>
          </span>
        </a>

        <div className="hidden items-center gap-8 md:flex">
          {LINKS.map((l) => (
            <a
              key={l.id}
              href={l.id}
              data-testid={`nav-link-${l.id.replace("#", "")}`}
              onClick={(e) => go(e, l.id)}
              className="font-display text-lg font-semibold uppercase tracking-wide text-bone/70 transition-colors duration-200 hover:text-bone"
            >
              {l.label}
            </a>
          ))}
          <a
            href="tel:+13528973160"
            data-testid="nav-phone-link"
            className="flex items-center gap-2 border border-bone/20 px-4 py-2 font-display text-lg font-semibold uppercase tracking-wide text-bone transition-colors duration-200 hover:border-bone"
          >
            <Phone className="h-4 w-4" />
            (352) 897-3160
          </a>
          <a
            href="#contact"
            data-testid="nav-schedule-button"
            onClick={(e) => go(e, "#contact")}
            className="bg-navy px-5 py-2 font-display text-lg font-bold uppercase tracking-wide text-white transition-colors duration-200 hover:bg-bone hover:text-ink"
          >
            Schedule Service
          </a>
        </div>

        <button
          className="text-bone md:hidden"
          data-testid="nav-mobile-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen(!open)}
        >
          {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="border-t border-bone/10 bg-charcoal md:hidden"
          >
            <div className="flex flex-col px-5 py-6">
              {LINKS.map((l) => (
                <a
                  key={l.id}
                  href={l.id}
                  data-testid={`nav-mobile-link-${l.id.replace("#", "")}`}
                  onClick={(e) => {
                    go(e, l.id);
                    setOpen(false);
                  }}
                  className="border-b border-bone/10 py-4 font-display text-3xl font-bold uppercase tracking-[0.02em] text-bone"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="tel:+13528973160"
                data-testid="nav-mobile-phone-link"
                className="mt-6 bg-navy px-5 py-3 text-center font-display text-xl font-bold uppercase tracking-wide text-white"
              >
                Call (352) 897-3160
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
